import React, { useEffect } from 'react';
import jsPDF from 'jspdf';

interface ExportAddressLabelReportProps {
    data: any;
    setPdfBlob: (blob: Blob) => void;
}

// ─── Avery 5160 Specifications (in mm) ───────────────────────────────────────
// Page:         Letter 215.9 × 279.4 mm  (portrait)
// Label size:   66.675 × 25.4 mm
// Top margin:   12.7 mm
// Left margin:  4.763 mm
// Horizontal gap between columns: 3.175 mm
// Column pitch = 66.675 + 3.175 = 69.85 mm
// Row pitch    = 25.4 mm (labels touch vertically)
// Columns: 3 | Rows: 10 | Labels per page: 30
// ─────────────────────────────────────────────────────────────────────────────

const AVERY_5160 = {
    pageW: 215.9,
    pageH: 279.4,
    labelW: 66.675,
    labelH: 25.4,
    topMargin: 12.7,
    leftMargin: 4.763,
    colGap: 3.175,
    cols: 3,
    rows: 10,
};

const ExportAddressLabelReport: React.FC<ExportAddressLabelReportProps> = ({ data, setPdfBlob }) => {

    const generatePDF = () => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: [AVERY_5160.pageW, AVERY_5160.pageH],
        });

        const { labelW, labelH, topMargin, leftMargin, colGap, cols, rows } = AVERY_5160;
        const colPitch = labelW + colGap;  // 69.85 mm
        const rowPitch = labelH;           // 25.4 mm

        const labelsPerPage = cols * rows; // 30

        // Filter out completely empty records — no blank boxes in output
        const validData = data.filter((item: any) => {
            const name = [item.FirstName, item.LastName].filter(Boolean).join('').trim();
            const street = (item.Address || item.Street || '').trim();
            const city = (item.City || '').trim();
            const state = (item.State || '').trim();
            const zip = (item.Zipcode || item.ZipCode || '').trim();
            return name || street || city || state || zip;
        });

        if (validData.length === 0) {
            const pdfBlob = doc.output('blob');
            setPdfBlob(pdfBlob);
            return;
        }

        // Padding inside each label
        const padX = 3;
        const padY = 3.5;
        const lineH = 4.2; // mm per text line

        doc.setFont('helvetica', 'normal');

        for (let i = 0; i < validData.length; i++) {
            const item = validData[i];

            // Start new page after every 30 labels (skip first page creation)
            if (i > 0 && i % labelsPerPage === 0) {
                doc.addPage();
            }

            const pageIndex = i % labelsPerPage;
            const col = pageIndex % cols;
            const row = Math.floor(pageIndex / cols);

            const x = leftMargin + col * colPitch;
            const y = topMargin + row * rowPitch;

            // ── Draw label border (light gray, thin) ──
            doc.setDrawColor(180, 180, 180);
            doc.setLineWidth(0.15);
            doc.roundedRect(x, y, labelW, labelH, 1.5, 1.5, 'S');

            // ── Label content ──
            const name = [item.FirstName, item.LastName].filter(Boolean).join(' ');
            const street = item.Address || item.Street || '';
            const cityStateZip = [
                item.City,
                item.State,
                item.Zipcode || item.ZipCode
            ].filter(Boolean).join(', ');

            // Build lines array (skip empties)
            const lines: { text: string; bold: boolean; size: number }[] = [];
            if (name) lines.push({ text: name, bold: true, size: 7.5 });
            if (street) lines.push({ text: street, bold: false, size: 7 });
            if (cityStateZip) lines.push({ text: cityStateZip, bold: false, size: 7 });

            // Calculate total block height to vertically center
            const totalBlockH = lines.length * lineH;
            const startY = y + (labelH - totalBlockH) / 2 + lineH * 0.7; // baseline offset

            // Center X of label
            const centerX = x + labelW / 2;

            lines.forEach((line, idx) => {
                doc.setFontSize(line.size);
                doc.setFont('helvetica', line.bold ? 'bold' : 'normal');
                doc.text(line.text, centerX, startY + idx * lineH, { align: 'center', maxWidth: labelW - 4 });
            });
        }

        const pdfBlob = doc.output('blob');
        setPdfBlob(pdfBlob);
    };

    useEffect(() => {
        if (data && data.length > 0) {
            generatePDF();
        }
    }, [data]);

    return null;
};

export default ExportAddressLabelReport;
